import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';

export const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
    const [categories, setCategories] = useState([]);
    const [topCategories, setTopCategories] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [subcategoriesMap, setSubcategoriesMap] = useState(() => {
        // Initialize from localStorage if available
        try {
            const stored = localStorage.getItem('subcategoriesMap');
            return (stored && stored !== 'undefined') ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error parsing subcategoriesMap from localStorage:', error);
            localStorage.removeItem('subcategoriesMap');
            return {};
        }
    });
    const [isServerDown, setIsServerDown] = useState(false);
    const loadingSubcategories = useRef(new Set());
    const initialLoadComplete = useRef(false);

    const fetchSubcategories = useCallback(async (categoryId) => {
        // Return cached data if available
        if (subcategoriesMap[categoryId]) {

            return subcategoriesMap[categoryId];
        }

        // Skip if already loading or server is down
        if (loadingSubcategories.current.has(categoryId) || isServerDown) {

            return null;
        }

        try {
            loadingSubcategories.current.add(categoryId);

            const response = await fetch(`${config.backendUrl}/products/main-category/categories/view/${categoryId}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch subcategories: ${response.status}`);
            }

            const data = await response.json();

            // Update state and localStorage atomically
            setSubcategoriesMap(prev => {
                const updated = { ...prev, [categoryId]: data };
                try {
                    localStorage.setItem('subcategoriesMap', JSON.stringify(updated));
                    localStorage.setItem('subcategoriesMapTimestamp', Date.now().toString());
                } catch (storageError) {

                }
                return updated;
            });

            return data;
        } catch (error) {
            console.error(`Error fetching subcategories for category ${categoryId}:`, error);
            // Return empty array instead of null to prevent UI errors
            return [];
        } finally {
            loadingSubcategories.current.delete(categoryId);
        }
    }, [isServerDown, subcategoriesMap]);

    const initializeCategories = useCallback(async () => {
        if (initialLoadComplete.current) return;

        console.log('[CategoryContext] Initializing categories, backendUrl:', config.backendUrl);

        try {
            // Check cache timestamp (24 hour TTL)
            const cacheTimestamp = localStorage.getItem('categoriesCacheTimestamp');
            const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
            const isCacheValid = cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < CACHE_TTL;

            // Try to load from localStorage first
            const stored = {
                categories: localStorage.getItem('categories'),
                topCategories: localStorage.getItem('topCategories'),
                categoryList: localStorage.getItem('categoryList')
            };

            if (isCacheValid && stored.categories && stored.topCategories && stored.categoryList) {
                try {
                    setCategories(JSON.parse(stored.categories));
                    setTopCategories(JSON.parse(stored.topCategories));
                    setCategoryList(JSON.parse(stored.categoryList));
                    setIsLoading(false);
                    initialLoadComplete.current = true;
                } catch (error) {
                    console.error('Error parsing cached categories:', error);
                    // Clear invalid cache and fetch fresh data
                    localStorage.removeItem('categories');
                    localStorage.removeItem('topCategories');
                    localStorage.removeItem('categoryList');
                    localStorage.removeItem('categoriesCacheTimestamp');
                    // Continue to fetch fresh data below
                    throw error;
                }
                
                // Refresh in background if cache is older than 6 hours
                const sixHours = 6 * 60 * 60 * 1000;
                if (cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) > sixHours) {

                    setTimeout(() => {
                        initialLoadComplete.current = false;
                        initializeCategories();
                    }, 1000);
                }
                return;
            }

            // Fetch fresh data if not in localStorage
            console.log('[CategoryContext] Fetching categories from:', `${config.backendUrl}/products/main-categories/`);
            const [categoriesRes, topCategoriesRes, categoryListRes] = await Promise.all([
                fetch(`${config.backendUrl}/products/main-categories/`),
                fetch(`${config.backendUrl}/products/top-categories/`),
                fetch(`${config.backendUrl}/products/main-categories/`)
            ]);
            console.log('[CategoryContext] Response status:', categoriesRes.status, topCategoriesRes.status, categoryListRes.status);

            // Check if all responses are ok
            if (!categoriesRes.ok || !topCategoriesRes.ok || !categoryListRes.ok) {
                const errorStatus = categoriesRes.status || topCategoriesRes.status || categoryListRes.status;
                throw new Error(`HTTP Error: ${errorStatus}`);
            }

            const [categoriesData, topCategoriesData, categoryListData] = await Promise.all([
                categoriesRes.json(),
                topCategoriesRes.json(),
                categoryListRes.json()
            ]);

            // Update state
            // Normalize category objects to { id, name, icon }
            const normalized = (Array.isArray(categoriesData) ? categoriesData : []).map(c => ({ id: c.id, name: c.name || c.category_name || '', icon: c.icon || c.image }));
            setCategories(normalized);
            setTopCategories(topCategoriesData.slice(0, 5));
            setCategoryList(categoryListData);

            // Update localStorage with timestamp
            localStorage.setItem('categories', JSON.stringify(normalized));
            localStorage.setItem('topCategories', JSON.stringify(topCategoriesData.slice(0, 5)));
            localStorage.setItem('categoryList', JSON.stringify(categoryListData));
            localStorage.setItem('categoriesCacheTimestamp', Date.now().toString());

            // Fetch ALL subcategories at once in the background (parallel)
            const subcategoriesPromises = categoriesData.map(async (category) => {
                if (!subcategoriesMap[category.id]) {
                    try {
                        const subcats = await fetchSubcategories(category.id);
                        return { id: category.id, data: subcats };
                    } catch (error) {

                        return { id: category.id, data: [] };
                    }
                }
                return null;
            });

            // Wait for all subcategories to load (with timeout)
            Promise.race([
                Promise.allSettled(subcategoriesPromises),
                new Promise(resolve => setTimeout(resolve, 5000)) // 5 second timeout
            ]).then(() => {

            });

            initialLoadComplete.current = true;
        } catch (error) {
            console.error('[CategoryContext] Failed to initialize categories:', error);
            console.error('[CategoryContext] Error name:', error.name, 'message:', error.message);
            
            // Only set server down for genuine server/network issues
            const isNetworkError = error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('NetworkError');
            const isServerError = error.message.includes('HTTP Error: 5') || error.message.includes('500') || error.message.includes('502') || error.message.includes('503') || error.message.includes('504');
            
            if (isNetworkError || isServerError) {

                setIsServerDown(true);
            } else {

                // Try to use cached data if available
                const cachedCategories = localStorage.getItem('categories');
                const cachedTopCategories = localStorage.getItem('topCategories');
                const cachedCategoryList = localStorage.getItem('categoryList');
                
                if (cachedCategories && cachedTopCategories && cachedCategoryList) {

                    try {
                        setCategories(JSON.parse(cachedCategories));
                        setTopCategories(JSON.parse(cachedTopCategories));
                        setCategoryList(JSON.parse(cachedCategoryList));
                        initialLoadComplete.current = true;
                    } catch (parseError) {
                        console.error('Error parsing cached categories:', parseError);
                        localStorage.removeItem('categories');
                        localStorage.removeItem('topCategories');
                        localStorage.removeItem('categoryList');
                    }
                } else {

                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [fetchSubcategories]);

    // Initialize on mount
    useEffect(() => {
        initializeCategories();
    }, [initializeCategories]);

    return (
        <CategoryContext.Provider
            value={{
                categories,
                topCategories,
                categoryList,
                isLoading,
                fetchSubcategories,
                subcategoriesMap,
                isServerDown
            }}
        >
            {children}
        </CategoryContext.Provider>
    );
};